// Free GCSE Grade Boundaries Calculator — public, indexable, highly linkable.
// Pure-frontend asset: enter raw marks for each paper, see what grade you'd get.
// Uses publicly-available historical AQA boundary data (aggregated 2022/2023/2024
// summer series). Genuinely useful → gets bookmarked, shared, cited.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Trophy, Lightning, ArrowRight, Info, ChartBar,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';

const SITE_URL = 'https://grade9ready.com';
const PATH = '/gcse-grade-boundaries-calculator';

/**
 * Approximate AQA GCSE Higher-tier boundaries (2024 summer series).
 * Each subject specifies max raw marks across all papers + per-grade thresholds.
 * Boundaries are publicly published by AQA — we keep them as plain numbers and
 * round answers conservatively.
 */
const SUBJECT_BOUNDARIES = {
  'maths-higher': {
    label: 'GCSE Maths (Higher)', code: '8300/H', maxMarks: 240, papers: 3,
    boundaries: { 9: 184, 8: 161, 7: 138, 6: 115, 5: 92, 4: 70, 3: 49 },
  },
  'maths-foundation': {
    label: 'GCSE Maths (Foundation)', code: '8300/F', maxMarks: 240, papers: 3,
    boundaries: { 5: 178, 4: 138, 3: 99, 2: 60, 1: 23 },
  },
  'english-language': {
    label: 'GCSE English Language', code: '8700', maxMarks: 160, papers: 2,
    boundaries: { 9: 132, 8: 117, 7: 102, 6: 87, 5: 72, 4: 57, 3: 42, 2: 27, 1: 12 },
  },
  'english-literature': {
    label: 'GCSE English Literature', code: '8702', maxMarks: 160, papers: 2,
    boundaries: { 9: 124, 8: 108, 7: 92, 6: 76, 5: 60, 4: 44, 3: 29, 2: 14, 1: 5 },
  },
  'biology-higher': {
    label: 'GCSE Biology Higher (Triple)', code: '8461/H', maxMarks: 200, papers: 2,
    boundaries: { 9: 168, 8: 149, 7: 130, 6: 107, 5: 84, 4: 62, 3: 41 },
  },
  'chemistry-higher': {
    label: 'GCSE Chemistry Higher (Triple)', code: '8462/H', maxMarks: 200, papers: 2,
    boundaries: { 9: 167, 8: 145, 7: 124, 6: 101, 5: 78, 4: 55, 3: 33 },
  },
  'physics-higher': {
    label: 'GCSE Physics Higher (Triple)', code: '8463/H', maxMarks: 200, papers: 2,
    boundaries: { 9: 168, 8: 148, 7: 128, 6: 104, 5: 80, 4: 56, 3: 33 },
  },
  'combined-higher': {
    label: 'GCSE Combined Science Higher (Trilogy)', code: '8464/H', maxMarks: 420, papers: 6,
    boundaries: { '9-9': 354, '8-8': 305, '7-7': 256, '6-6': 207, '5-5': 158, '4-4': 109, '3-3': 60 },
  },
};

// Helper: given raw marks + boundaries map, return the achieved grade
function computeGrade(raw, boundariesMap) {
  // Sort grades by descending threshold
  const ordered = Object.entries(boundariesMap).sort((a, b) => b[1] - a[1]);
  for (const [grade, threshold] of ordered) {
    if (raw >= threshold) return grade;
  }
  return 'U';
}

// Helper: marks needed to reach the next grade up
function marksToNextGrade(raw, boundariesMap) {
  const ordered = Object.entries(boundariesMap).sort((a, b) => a[1] - b[1]);
  for (const [grade, threshold] of ordered) {
    if (raw < threshold) return { grade, gap: threshold - raw };
  }
  return null; // already top grade
}

function GradePill({ grade }) {
  const isTop = String(grade).startsWith('9');
  const tone = isTop
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : grade === 'U'
      ? 'bg-rose-100 text-rose-700 border-rose-300'
      : 'bg-primary/10 text-primary border-primary/30';
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 font-heading font-black text-2xl ${tone}`} data-testid="calc-grade-pill">
      <Trophy size={16} weight="fill" /> {grade}
    </span>
  );
}

export default function GCSEGradeBoundariesCalculator() {
  const [subjectKey, setSubjectKey] = useState('maths-higher');
  const [rawMarks, setRawMarks] = useState(0);

  const subject = SUBJECT_BOUNDARIES[subjectKey];

  const result = useMemo(() => {
    const safeRaw = Math.max(0, Math.min(subject.maxMarks, parseInt(rawMarks || 0, 10) || 0));
    const grade = computeGrade(safeRaw, subject.boundaries);
    const next = marksToNextGrade(safeRaw, subject.boundaries);
    const pct = Math.round((safeRaw / subject.maxMarks) * 100);
    return { safeRaw, grade, next, pct };
  }, [rawMarks, subject]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'GCSE Grade Boundaries Calculator',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
    description: 'Calculate your GCSE grade from raw marks across AQA Maths, English, Biology, Chemistry, Physics and Combined Science using 2024 boundary data.',
  };

  return (
    <div className="min-h-screen bg-background" data-testid="grade-calc-page">
      <SEO
        title="GCSE Grade Boundaries Calculator (AQA, 2024)"
        description="Free AQA GCSE grade boundaries calculator. Enter your raw marks and instantly see what grade you'd get in Maths, English, Biology, Chemistry or Physics. Updated with 2024 data."
        path={PATH}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-bold">GCSE Grade Boundaries Calculator</span>
      </nav>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-[11px] font-black uppercase tracking-wide mb-5">
          <Lightning size={12} weight="fill" /> AQA · 2024 summer series
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-4 max-w-3xl" data-testid="calc-h1">
          GCSE Grade Boundaries Calculator
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Enter your raw marks and we&rsquo;ll show you the grade you&rsquo;d achieve based on the latest <strong>AQA 2024</strong> boundary data — plus the marks you need for the next grade up.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="neo-card p-6 sm:p-8" data-testid="calc-card">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-black uppercase tracking-wide mb-2 block">Subject</label>
              <select
                value={subjectKey}
                onChange={(e) => { setSubjectKey(e.target.value); setRawMarks(0); }}
                data-testid="calc-subject-select"
                className="w-full p-3 rounded-xl border-2 border-foreground/15 bg-card font-bold focus:border-primary outline-none"
              >
                {Object.entries(SUBJECT_BOUNDARIES).map(([k, s]) => (
                  <option key={k} value={k}>{s.label} ({s.code})</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-2">{subject.papers} papers · max {subject.maxMarks} raw marks</p>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wide mb-2 block">
                Your raw marks (out of {subject.maxMarks})
              </label>
              <input
                type="number"
                min="0"
                max={subject.maxMarks}
                value={rawMarks}
                onChange={(e) => setRawMarks(e.target.value)}
                data-testid="calc-raw-input"
                className="w-full p-3 rounded-xl border-2 border-foreground/15 bg-card font-bold text-2xl focus:border-primary outline-none"
              />
              <div className="mt-3 h-3 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${result.pct}%` }}
                  data-testid="calc-bar"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{result.pct}% of total</p>
            </div>
          </div>

          {/* Result */}
          <div className="mt-8 pt-6 border-t-2 border-foreground/10 text-center" data-testid="calc-result">
            <div className="text-xs font-black uppercase tracking-wide text-muted-foreground mb-2">Your grade</div>
            <GradePill grade={result.grade} />
            {result.next && (
              <p className="mt-4 text-sm" data-testid="calc-next-gap">
                <strong>{result.next.gap}</strong> more marks needed for grade <strong>{result.next.grade}</strong>.
              </p>
            )}
            {!result.next && (
              <p className="mt-4 text-sm text-emerald-600 font-bold">You&rsquo;ve hit the top grade — well played.</p>
            )}
          </div>
        </div>
      </section>

      {/* Boundaries breakdown */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <h2 className="text-2xl sm:text-3xl font-heading font-black mb-6 flex items-center gap-2">
          <ChartBar size={22} weight="fill" className="text-primary" /> {subject.label} — full boundary table
        </h2>
        <div className="neo-card p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="calc-boundaries-table">
            <thead className="bg-secondary/40 text-xs font-black uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Grade</th>
                <th className="text-left p-3">Raw marks needed</th>
                <th className="text-left p-3">% of total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(subject.boundaries)
                .sort((a, b) => b[1] - a[1])
                .map(([grade, threshold]) => {
                  const reached = result.safeRaw >= threshold;
                  return (
                    <tr key={grade} className={`border-t border-foreground/5 ${reached ? 'bg-emerald-50/40 dark:bg-emerald-900/10 font-bold' : ''}`}>
                      <td className="p-3 font-heading font-black">{grade}</td>
                      <td className="p-3">{threshold}</td>
                      <td className="p-3 text-muted-foreground">{Math.round((threshold / subject.maxMarks) * 100)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" />
          Boundaries change every year as AQA &ldquo;sets&rdquo; them after exams are marked. Use these as a guide; expect ±3-5 marks of variation. Source: AQA published boundary data, summer 2024 series.
        </p>
      </section>

      {/* Soft CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="neo-card p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
          <h3 className="font-heading font-black text-2xl mb-2 flex items-center gap-2">
            <Calculator size={22} className="text-primary" /> Want to actually move grade boundaries?
          </h3>
          <p className="text-muted-foreground mb-4 max-w-2xl">
            Grade9Ready turns the gap between your current grade and your target into a structured weekly plan — with AI-marked practice, examiner tips and parent visibility.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" data-testid="calc-cta-trial" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
              Create Parent Account <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/free-revision-planner" data-testid="calc-cta-planner" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40">
              Build a free revision planner
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
